import React from "react";
import { DispatchProp } from "react-redux";
import { isEmpty } from "lodash";
import { ChoiceModel } from "../../models/ChoiceModel";
import { MentionModel } from "../../models/MentionModel";
import { QuestionModel } from "../../models/QuestionModel";
import { MentionBuilderDelegate } from "../../models/builders/MentionBuilder";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ThemeModel } from "../../models/ThemeModel";
import { VariableModel } from "../../models/VariableModel";
import {
    setMultipleVariablesMentions,
    setQuestionFieldStatus,
    setVariableMentions,
} from "../../redux/actions";
import { __getStore__ } from "../../redux/store";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { QuestionType } from "../../models/enums/QuestionType";
import { isNullOrEmpty } from "../../utils";

export interface ChoiceHandler {
    select(): void;
}

export type ChoiceRef = React.MutableRefObject<ChoiceHandler>;

export interface ChoiceProps {
    // Store state
    question: QuestionModel;
    questionnaire: QuestionnaireModel;
    mentions: MentionModel[];
    theme: ThemeModel;
    language: string;
    matrix: number;
    // Props
    choice: ChoiceModel;
    choiceRef: ChoiceRef;
    variable: VariableModel;
    gridRendering?: boolean;
    openEnd?: string;
    isFirst?: boolean;
    isLast?: boolean;
    horizontal?: boolean;
    label?: string;
    isDisabled?: boolean;
    isSelected?: boolean;
    executeUpdateForIdAfterSelection?: string;
}

export abstract class BaseChoice<P, S> extends React.Component<P & ChoiceProps & DispatchProp, S> {
    setMentions(variableName: string, builder: MentionBuilderDelegate, matrixIndex?: number) {
        const { matrix, dispatch } = this.props;
        const index = matrixIndex || matrix;
        dispatch(setVariableMentions(variableName, builder, index));
    }

    getMention = (mentionIndex?: number) => {
        const { mentions } = this.props;
        return mentions[mentionIndex || 0];
    };

    getMentionValues = () => {
        const { mentions } = this.props;
        return mentions.map((m) => m.value);
    };

    getMentionValue = (mentionIndex?: number) =>
        this.getMention(mentionIndex) === undefined ? "" : this.getMention(mentionIndex).value;

    getMentionOpenEnd = (mentionIndex?: number) =>
        this.getMention(mentionIndex) === undefined ? "" : this.getMention(mentionIndex).openEnd;

    getLatestMentionsForVariableName = (variableName: string) => {
        return __getStore__().getState().interview.variables.mentionsByVariableName[variableName];
    };

    updateMentionsAfterDisablingFields = (variableNames: any) => {
        const { dispatch } = this.props;
        dispatch(setMultipleVariablesMentions(variableNames));
    };

    updateQuestionFieldDisabledStatus = (ids: any, disabled: boolean) => {
        const { dispatch } = this.props;
        dispatch(setQuestionFieldStatus(ids, disabled));
    };

    disableAndResetHybridQuestionMapppedCell = (currentFieldIdentifier: string) => {
        const { question } = this.props;

        if (
            question?.parent?.type === QuestionType.HybridGrid &&
            !isNullOrEmpty(currentFieldIdentifier)
        ) {
            const [questionId, currentFieldVariableName] = currentFieldIdentifier.split("&");
            const disabledFieldKeys = [];
            const enableFieldKeys = [];
            const disableColumnsList = [];

            // To reset multiple mentions at once
            let mentionsByVariable: { [variableName: string]: MentionBuilderDelegate } = {};
            const addMention = (variableName: string, builder: MentionBuilderDelegate) => {
                mentionsByVariable = {
                    ...mentionsByVariable,
                    [variableName]: builder,
                };
            };

            const rows = question?.parent?.rows;
            const filteredRows = rows.filter(
                (x) =>
                    x.variables &&
                    x.variables.length > 0 &&
                    x.variables.find((vb) => vb.name === currentFieldVariableName),
            );

            if (filteredRows && filteredRows.length > 0) {
                // Get columns to be disabled for entire row if there is any mention selected for it.
                filteredRows[0].variables.forEach((loopVariable) => {
                    if (loopVariable.choiceList?.length > 0) {
                        const mention = this.getLatestMentionsForVariableName(loopVariable.name);

                        if (mention && mention.length > 0) {
                            loopVariable.choiceList.forEach((choice) => {
                                if (mention && mention.some((x) => x.value === choice.value)) {
                                    disableColumnsList.push(
                                        getSettingString(choice, "DisableColumns"),
                                    );
                                }
                            });
                        }
                    }
                });

                // Don't add filter for current variablename as index will vary
                filteredRows[0].variables.forEach((loopVariable, index) => {
                    const mappedFieldIdentifier = `${questionId}&${loopVariable.name}`;

                    // enableFieldKeys store all keys which will enable all column and disabledFieldKeys will disable based on choice selection again.
                    if (disableColumnsList && disableColumnsList.length > 0) {
                        enableFieldKeys.push(mappedFieldIdentifier);

                        const isDisabled = disableColumnsList
                            .join(",")
                            .replace(/\s/g, "")
                            .split(",")
                            .some((c) => c === index.toString());

                        if (isDisabled && currentFieldIdentifier !== mappedFieldIdentifier) {
                            disabledFieldKeys.push(mappedFieldIdentifier);

                            // Check latest mentions and reset only if there is any
                            const currentMentions = this.getLatestMentionsForVariableName(
                                loopVariable.name,
                            );

                            if (currentMentions && currentMentions.length > 0) {
                                addMention(loopVariable.name, (m) => m.clear());
                            }
                        }
                    } else {
                        enableFieldKeys.push(mappedFieldIdentifier);
                    }
                });
            }

            // First enable all column then disable as per selection and questionairre settings
            if (!isEmpty(enableFieldKeys)) {
                this.updateQuestionFieldDisabledStatus(enableFieldKeys, false);
            }

            if (!isEmpty(disabledFieldKeys)) {
                this.updateQuestionFieldDisabledStatus(disabledFieldKeys, true);
            }

            if (!isEmpty(mentionsByVariable)) {
                this.updateMentionsAfterDisablingFields(mentionsByVariable);
            }
        }
    };

    abstract renderChoice(choice: ChoiceModel);

    render() {
        const { choice } = this.props;
        return this.renderChoice(choice);
    }
}
